import api from './api-backend';
import type {
  TourWithProgress,
  Tour,
  TourStep,
  CreateTourPayload,
  UpdateTourPayload,
  CreateStepPayload,
  UpdateStepPayload,
  ReorderStepsPayload,
  PublishTourPayload,
} from 'types/tour';

interface ApiMeta {
  status: string;
  message: string;
}

interface ApiResponse<T> {
  meta: ApiMeta;
  data: T;
}

interface PaginatedResponse<T> {
  meta: ApiMeta;
  data: T[];
  pagination: {
    current_page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
  };
}

// ─── User-facing endpoints ────────────────────────────────────────────────────

const tourService = {
  /** Get all active tours for a route (including completed). Used by HelpButton. */
  getAllTours(route: string): Promise<ApiResponse<TourWithProgress[]>> {
    return api.get(`/v1/tours?route=${encodeURIComponent(route)}`).then(r => r.data);
  },

  /** Get tours the user hasn't completed (or has a new version). Used for auto-trigger. */
  getPendingTours(route: string): Promise<ApiResponse<TourWithProgress[]>> {
    return api.get(`/v1/tours/pending?route=${encodeURIComponent(route)}`).then(r => r.data);
  },

  /** Mark a tour as completed for current user */
  completeTour(tourId: string): Promise<ApiResponse<null>> {
    return api.post(`/v1/tours/${tourId}/complete`).then(r => r.data);
  },

  /** Dismiss a tour (won't show again until version bump) */
  dismissTour(tourId: string): Promise<ApiResponse<null>> {
    return api.post(`/v1/tours/${tourId}/dismiss`).then(r => r.data);
  },

  /** Reset tour progress so it shows again */
  resetTour(tourId: string): Promise<ApiResponse<null>> {
    return api.post(`/v1/tours/${tourId}/reset`).then(r => r.data);
  },
};

// ─── Admin endpoints ──────────────────────────────────────────────────────────

export const adminTourService = {
  listTours(params?: {
    route?: string;
    active?: 'true' | 'false';
    page?: number;
    per_page?: number;
  }): Promise<PaginatedResponse<Tour>> {
    const q = new URLSearchParams();
    if (params?.route) q.append('route', params.route);
    if (params?.active) q.append('active', params.active);
    if (params?.page) q.append('page', String(params.page));
    if (params?.per_page) q.append('per_page', String(params.per_page));
    const qs = q.toString();
    return api.get(`/v1/admin/tours${qs ? `?${qs}` : ''}`).then(r => r.data);
  },

  getTour(id: string): Promise<ApiResponse<Tour>> {
    return api.get(`/v1/admin/tours/${id}`).then(r => r.data);
  },

  createTour(payload: CreateTourPayload): Promise<ApiResponse<Tour>> {
    return api.post('/v1/admin/tours', payload).then(r => r.data);
  },

  updateTour(id: string, payload: UpdateTourPayload): Promise<ApiResponse<Tour>> {
    return api.put(`/v1/admin/tours/${id}`, payload).then(r => r.data);
  },

  deleteTour(id: string): Promise<ApiResponse<null>> {
    return api.delete(`/v1/admin/tours/${id}`).then(r => r.data);
  },

  publishTour(id: string, payload?: PublishTourPayload): Promise<ApiResponse<Tour>> {
    return api.post(`/v1/admin/tours/${id}/publish`, payload ?? {}).then(r => r.data);
  },

  addStep(tourId: string, payload: CreateStepPayload): Promise<ApiResponse<TourStep>> {
    return api.post(`/v1/admin/tours/${tourId}/steps`, payload).then(r => r.data);
  },

  updateStep(tourId: string, stepId: string, payload: UpdateStepPayload): Promise<ApiResponse<TourStep>> {
    return api.put(`/v1/admin/tours/${tourId}/steps/${stepId}`, payload).then(r => r.data);
  },

  deleteStep(tourId: string, stepId: string): Promise<ApiResponse<null>> {
    return api.delete(`/v1/admin/tours/${tourId}/steps/${stepId}`).then(r => r.data);
  },

  reorderSteps(tourId: string, payload: ReorderStepsPayload): Promise<ApiResponse<TourStep[]>> {
    return api.put(`/v1/admin/tours/${tourId}/steps/reorder`, payload).then(r => r.data);
  },
};

export default tourService;
